import * as Tone from 'tone';

let native: AudioContext | null = null;
let context: Tone.Context | null = null;

/**
 * The one shared AudioContext for the whole app. It is a native AudioContext wrapped for Tone.js,
 * so playback (Tone), the microphone and the pitch-tracking AudioWorklet all run on the same
 * clock, and the output device can be chosen with setSinkId.
 */
export function audioContext(): Tone.Context {
  if (!context) {
    native = new AudioContext({ latencyHint: 'interactive' });
    context = new Tone.Context({ context: native, lookAhead: 0.05 });
    Tone.setContext(context);
  }
  return context;
}

/** The native AudioContext behind Tone's context (for the microphone and the AudioWorklet). */
export function rawAudioContext(): AudioContext {
  audioContext();
  return native as AudioContext;
}

/** Browsers start audio only after a user gesture: call this from a click handler. */
export async function resumeAudio(): Promise<void> {
  audioContext();
  await Tone.start();
}

type SinkContext = AudioContext & { setSinkId?: (id: string) => Promise<void> };

/** Whether the browser lets the app choose the output device (Chrome and Edge do). */
export function canChooseOutput(): boolean {
  return typeof AudioContext !== 'undefined' && 'setSinkId' in AudioContext.prototype;
}

/** Send all playback to an output device ('' for the system default). */
export async function setOutputDevice(deviceId: string): Promise<boolean> {
  const ctx = rawAudioContext() as SinkContext;
  if (!ctx.setSinkId) return false;
  try {
    await ctx.setSinkId(deviceId);
    return true;
  } catch {
    return false;
  }
}

/** The delay the browser reports between scheduling a sound and hearing it, plus input delay. */
export function reportedLatencySec(inputLatencySec = 0.01): number {
  const ctx = rawAudioContext();
  return (ctx.baseLatency || 0) + (ctx.outputLatency || 0) + inputLatencySec;
}
